/** Book helpers for Value Dashboard trade modal — mirrors GameTable bestBidAsk leg logic. */

import { centsToAmericanString } from '@/lib/pricing/american';
import { mirrorCents } from '@/lib/pricing/subCent';
import { hasPropSynthKey } from '@/lib/sport/propSynthKey';
import { venueSlot } from '@/lib/venue/venueSlot';
import { hasCfbEventSynthKey, cfbEventOutcomeLabel } from '@/lib/sport/cfbEventSynthKey';
import type { BookLevel, GameRow, MarketSide, TeamSide, Venue, VenueBook } from './types';

// Mirror of ladder/helpers.bidIsReal — kept inline because ladder/helpers
// imports bestPriceCentsForOrderSide from this module (importing back would be
// circular). KEEP IN SYNC. A bid level is "real" when it carries >=1 contract
// of size AND sits at/below the last trade plus a sanity slack. This drops the
// floating-point dust hydra's orderbook accumulates (e.g. a 32¢ level with
// size 1.99e-13), which a raw Math.max(book) would otherwise pick as the
// top-of-book — making the maker pill read a phantom (32) instead of the real
// best bid the ladder shows.
const LEVEL_SANITY_SLACK = 15;
function realBids(
  levels: BookLevel[] | undefined,
  lastTradeCents: number | null | undefined,
): BookLevel[] {
  if (!levels) return [];
  return levels.filter(
    (l) =>
      l.size >= 1 &&
      (lastTradeCents == null || l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK),
  );
}

/** Convert an implied probability in cents (1-99) to American odds string.
 *  Thin delegating shim — single source of truth lives in
 *  {@link centsToAmericanString} in lib/pricing/american (canonical "+100"
 *  at exactly 50¢ convention; "—" sentinel for boundaries). */
export function americanOdds(cents: number): string {
  return centsToAmericanString(cents);
}

export function bestYesBidCents(book: VenueBook | undefined): number | undefined {
  // YES bids filter against the YES last trade. Matches the ladder so the pill
  // shows the same top-of-book, not an FP-dust level.
  const real = realBids(book?.yes, book?.lastPriceCents);
  return real.length ? Math.max(...real.map((l) => l.priceCents)) : undefined;
}

export function bestNoBidCents(book: VenueBook | undefined): number | undefined {
  // NO bids filter against the NO last trade (100 − YES last), mirroring
  // ladder/helpers.bestBidDepthForSide.
  const noLast = book?.lastPriceCents == null ? null : 100 - book.lastPriceCents;
  const real = realBids(book?.no, noLast);
  return real.length ? Math.max(...real.map((l) => l.priceCents)) : undefined;
}

export function bestPriceCentsForOrderSide(
  book: VenueBook | undefined,
  orderSide: 'yes' | 'no',
): number | undefined {
  return orderSide === 'yes' ? bestYesBidCents(book) : bestNoBidCents(book);
}

/** Whether a limit at `priceCents` would rest (maker) or cross (taker), read
 * off the opposing side's best bid: YES ask = 100 − best NO bid and vice versa.
 * Missing opposing depth is unknown, not proof that the order is maker.
 *
 * Venue-agnostic despite its Polymarket origin — the `realBids` sanity filter
 * it leans on is the same one the Kalshi ladder uses, so the Kalshi order
 * ticket reads its fee perspective from here too (#535). Poly's own
 * maker/taker FEE role is a separate consumer of the same answer. */
export function limitOrderRole(
  book: VenueBook | undefined,
  orderSide: 'yes' | 'no',
  priceCents: number,
): 'maker' | 'taker' | null {
  const opposingBid = orderSide === 'yes' ? bestNoBidCents(book) : bestYesBidCents(book);
  if (opposingBid == null) return null;
  // `mirrorCents`, NOT `100 - opposingBid`. Every surface that RENDERS an ask
  // level mirrors it in the integer centi-cent domain (buildSide, the ladder's
  // bestAskCentsForBook), and 272 of the 999 decicent bid values disagree
  // between the two forms: a 64.1¢ NO bid gives an ask of 35.9 by mirror and
  // 35.900000000000006 by subtraction, so a click on the displayed 35.9¢ ask
  // failed `>=` and classified as a MAKER. That put the order ticket's odds on
  // the maker rate while the ladder ask row beside it used the taker rate —
  // the exact disagreement #535 removes. Same trap `lib/pricing/subCent` and
  // #452's `sideNativePriceCents` already document.
  const bestAsk = mirrorCents(opposingBid);
  return priceCents >= bestAsk ? 'taker' : 'maker';
}

export function opponentTeamCode(game: GameRow, teamSide: 'away' | 'home'): string {
  return teamSide === 'away' ? game.homeCode : game.awayCode;
}

/** Which market a GameRow represents, read off its key: a real moneyline row,
 *  or one of the synthetic rows the views mint for a single derived outcome
 *  (SpreadsTotals' `|spread|`/`|total|`, soccer's `|multi|`, and the shared
 *  player-prop `|prop-`). Used by the modal to swap "wins"-style labels
 *  for the synth's own bet description.
 *
 *  A kind with no case here falls through to 'moneyline' and is then described
 *  with the PARENT GAME's team codes — silently wrong on any market that is not
 *  a moneyline. That is the #251 defect; adding a synth kind means adding it
 *  here too.
 *
 *  The player-prop case is the one shared contract: `lib/sport/propSynthKey`
 *  owns the marker and this asks it via `hasPropSynthKey` (#566), so this
 *  matcher cannot drift off the sites that mint the key. NB the marker is
 *  `|prop-`, NOT `|prop|` — matching on `|prop|` silently never fires. */
export function gameMarketKind(game: GameRow): 'spread' | 'total' | 'multi' | 'prop' | 'moneyline' {
  if (game.usPlayerProp) return 'prop';
  if (game.key.includes('|spread|')) return 'spread';
  if (game.key.includes('|total|')) return 'total';
  if (game.key.includes('|multi|')) return 'multi';
  if (hasPropSynthKey(game.key)) return 'prop';
  // Football Game Props ladders use one binary book in both team slots.
  // Their YES/NO descriptions are the synth names, never either team winning.
  if (hasCfbEventSynthKey(game.key)) return 'prop';
  return 'moneyline';
}

/** The name that captions the order book for the currently selected team side.
 *
 *  Prop rungs are the exception the caller must not hand-roll: both venue slots
 *  carry the SAME book, so `teamSide` selects nothing and the panel's YES/NO
 *  toggle is the real control. Reading `homeName` for a prop would caption the
 *  book with the outcome's COMPLEMENT — "…does not get 6+ Strikeouts — YES" —
 *  which contradicts the book it is labelling. Reachable whenever the panel has
 *  no chip strip to suppress this caption — today that means a lone-rung
 *  ladder, `livePropPanel`'s other strip-withholding branch being documented
 *  there as unable to fire.
 *
 *  Multi/spread/total deliberately keep the away/home split: their two slots
 *  are genuinely two different outcomes, so the selected side is meaningful. */
export function bookCaptionName(game: GameRow, teamSide: 'away' | 'home'): string {
  if (gameMarketKind(game) === 'prop') return game.awayName;
  return teamSide === 'away' ? game.awayName : game.homeName;
}

const OUTCOME_UNAVAILABLE = '(outcome unavailable)';

/** Spread caption (#555). A spread market's YES outcome belongs to the
 *  FAVORED side, not a fixed slot — a home-favored line under the old
 *  YES = awayName rule captioned BUY YES with the underdog complement.
 *  Reads only the synthetic identity `synthesizeFromSpread` retains and
 *  validates it against the selected venue's exact book before naming a
 *  team; anything missing or contradictory fails closed to the neutral
 *  caption rather than risking the wrong outcome. The sign check is
 *  defensive — every producer signs `line` away-negative/home-positive
 *  (marketStore getGameEvents), so a mismatch means a corrupted row. */
function spreadOutcomeSideLabel(
  game: GameRow,
  teamSide: TeamSide,
  orderSide: MarketSide,
  venue: Venue | undefined,
): string {
  const identity = game.spreadOutcome;
  if (!identity || !venue || !Number.isFinite(identity.line) || identity.line === 0) {
    return OUTCOME_UNAVAILABLE;
  }

  const sideFromSign: TeamSide = identity.line < 0 ? 'away' : 'home';
  if (sideFromSign !== identity.favored) return OUTCOME_UNAVAILABLE;

  const book = venueBookForTeam(game, teamSide, venue);
  const expectedTicker =
    venue === 'kalshi'
      ? identity.kalshiTicker
      : venue === 'polymarket_us'
        ? identity.polymarketUsTicker ?? null
        : identity.polymarketTicker;
  if (!book || !expectedTicker || book.marketTicker !== expectedTicker) {
    return OUTCOME_UNAVAILABLE;
  }

  const yesLabel = identity.favored === 'away' ? game.awayName : game.homeName;
  const noLabel = identity.favored === 'away' ? game.homeName : game.awayName;
  if (!yesLabel.trim() || !noLabel.trim() || yesLabel === noLabel) {
    return OUTCOME_UNAVAILABLE;
  }

  return `(${orderSide === 'yes' ? yesLabel : noLabel})`;
}

/** Human outcome for the selected market ticker + YES/NO (buy side).
 *  For moneyline: "(CLE wins)". For total/multi/prop synths: derives from the
 *  synthetic GameRow's awayName/homeName which already encode the bet
 *  outcome ("Over 9.5", "CRY does not win",
 *  "Brandon Sproat (MIL) does not get 6+ Strikeouts") — YES side = awayName,
 *  NO side = homeName by synthesize convention.
 *
 *  Spread synths (#555) LEFT that convention: their YES outcome is the
 *  favored side's signed name, so the caption derives from the retained
 *  `spreadOutcome` identity + the caller's selected venue instead. Spread
 *  callers must pass `venue`; non-spread callers keep the 3-arg contract.
 *
 *  Player props (#251) join the slot convention rather than getting their own
 *  branch. Before, a prop key matched no case and fell through to the moneyline
 *  line below, so the ticket for a player's strikeout market named the PARENT
 *  GAME's team — a market that is neither team's, and not a moneyline. The
 *  synth's two slots must therefore carry DIFFERENT text; both minted together
 *  by `mlbPropRungLabels`. (Reproducible: give a `|prop-` synth to the
 *  moneyline branch and it returns "(<awayCode> wins)".) */
export function outcomeSideLabel(
  game: GameRow,
  teamSide: TeamSide,
  orderSide: MarketSide,
  venue?: Venue,
): string {
  const kind = gameMarketKind(game);
  if (hasCfbEventSynthKey(game.key)) return cfbEventOutcomeLabel(game, orderSide);
  if (kind === 'spread') {
    return spreadOutcomeSideLabel(game, teamSide, orderSide, venue);
  }
  if (kind === 'total' || kind === 'multi' || kind === 'prop') {
    return `(${orderSide === 'yes' ? game.awayName : game.homeName})`;
  }
  const teamCode = teamSide === 'away' ? game.awayCode : game.homeCode;
  const oppCode = opponentTeamCode(game, teamSide);
  if (orderSide === 'yes') return `(${teamCode} wins)`;
  return `(${oppCode} wins)`;
}

export function venueBookForTeam(
  game: GameRow,
  teamSide: 'away' | 'home',
  venue: Venue,
): VenueBook | undefined {
  return venueSlot(game, venue)?.[teamSide];
}
